import type { MatchState, PlayerState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { EntityId, TeamIdx } from '../types';
import { PITCH } from '../config/pitchConfig';
import { slotRole, type Role } from '../ai/formations';

// Auto-switching relevance (§8.2). Pure — scores how much a player "wants" control this tick, so the router
// can move an auto session onto the most relevant own-team entity. No I/O, no RNG.
//
//   relevance(p) = W_DIST/(1+dist(p,ball))
//                + W_INTERCEPT·max(0, closingSpeedAlongBallLine)/MAX_SPEED
//                + W_GOALSIDE·goalSide01(p,ball,ownGoal)   // defending only
//                + roleBias[role][ballThird]
//                − (isGk ? GK_PENALTY : 0)                 // GK_PENALTY drops to GK_PENALTY_BOX when ball in own box

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Is the ball inside `team`'s own penalty box? */
function ballInOwnBox(state: MatchState, team: TeamIdx): boolean {
  const pitch = PITCH[state.mode.teamSize];
  const dir = state.attackDir[team];
  const ownGoalX = -dir * (pitch.length / 2);
  const xFromGoal = dir * (state.ball.pos.x - ownGoalX);
  return xFromGoal >= 0 && xFromGoal <= pitch.penaltyBoxDepth && Math.abs(state.ball.pos.y) <= pitch.penaltyBoxHalfWidth;
}

/** Ball third from `team`'s perspective: 0 = defensive, 1 = middle, 2 = attacking. */
function ballThird(state: MatchState, team: TeamIdx): 0 | 1 | 2 {
  const pitch = PITCH[state.mode.teamSize];
  const n = (state.attackDir[team] * state.ball.pos.x) / (pitch.length / 2); // −1 own goal → +1 opponent goal
  return n < -1 / 3 ? 0 : n > 1 / 3 ? 2 : 1;
}

function roleBiasFor(cfg: SimConfig, role: Role, third: 0 | 1 | 2): number {
  if (role === 'DEF') return cfg.switching.roleBias.DEF[third];
  if (role === 'FWD') return cfg.switching.roleBias.FWD[third];
  if (role === 'MID') return cfg.switching.roleBias.MID[third];
  return 0; // GK carries no role bias (its penalty dominates)
}

export function relevanceScore(state: MatchState, p: PlayerState, cfg: SimConfig): number {
  const sw = cfg.switching;
  const b = state.ball;
  const dx = b.pos.x - p.pos.x;
  const dy = b.pos.y - p.pos.y;
  const dist = Math.hypot(dx, dy);

  let score = sw.W_DIST / (1 + dist);

  // closing speed: component of the player's velocity toward the ball, normalized to top speed
  if (dist > 1e-6) {
    const ux = dx / dist;
    const uy = dy / dist;
    const closing = p.vel.x * ux + p.vel.y * uy;
    if (closing > 0) score += sw.W_INTERCEPT * Math.min(1, closing / cfg.movement.MAX_SPEED);
  }

  // goal-side bias, only while defending (an opponent holds the ball)
  const possessor = b.possessor !== null ? state.players[b.possessor] : undefined;
  const defending = possessor !== undefined && possessor.team !== p.team;
  if (defending) {
    const pitch = PITCH[state.mode.teamSize];
    const ownGoalX = -state.attackDir[p.team] * (pitch.length / 2);
    const gx = ownGoalX - b.pos.x;
    const gy = 0 - b.pos.y;
    const glen = Math.hypot(gx, gy);
    if (glen > 1e-6) {
      const t = ((p.pos.x - b.pos.x) * gx + (p.pos.y - b.pos.y) * gy) / (glen * glen); // 0 at ball, 1 at own goal
      score += sw.W_GOALSIDE * clamp01(t);
    }
  }

  const size = state.mode.teamSize;
  const role = slotRole(size, p.id - p.team * size);
  score += roleBiasFor(cfg, role, ballThird(state, p.team));

  // D123 dead-ball term: the designated taker of YOUR OWN restart is the entity you want. Applied last
  // and above GK_PENALTY, because at a goal kick the taker IS the keeper — without this the keeper's own
  // penalty guaranteed Tab handed you an outfielder while he stood over the ball.
  if (state.restart !== null && state.restart.taker === p.id && state.restart.team === p.team) score += sw.W_TAKER;

  if (p.isGk) score -= ballInOwnBox(state, p.team) ? sw.GK_PENALTY_BOX : sw.GK_PENALTY;

  return score;
}

/**
 * Pick the entity an auto session should control (§8.2), or `null` to stay put. Chooses the most relevant
 * own-team, on-pitch, non-human-held entity; switches only if it beats the current holder's relevance by
 * more than H_MARGIN and the cooldown has elapsed. Ties keep the lower EntityId (deterministic).
 */
export function pickAutoSwitch(
  state: MatchState,
  team: TeamIdx,
  currentId: EntityId | null,
  ticksSinceSwitch: number,
  humanHeld: ReadonlySet<EntityId>,
  cfg: SimConfig,
): EntityId | null {
  const size = state.mode.teamSize;
  let bestId: EntityId | null = null;
  let bestScore = -Infinity;
  for (let slot = 0; slot < size; slot++) {
    const id = (team * size + slot) as EntityId;
    const p = state.players[id];
    if (!p || p.sentOff) continue;
    if (id !== currentId && humanHeld.has(id)) continue; // never a another human's entity
    const s = relevanceScore(state, p, cfg);
    if (s > bestScore) {
      bestScore = s;
      bestId = id;
    }
  }
  if (bestId === null || bestId === currentId) return null;
  if (ticksSinceSwitch <= cfg.switching.H_COOLDOWN) return null; // post-switch lockout not elapsed
  const current = currentId !== null ? state.players[currentId] : undefined;
  // stickiness: bias toward the current holder so a marginally-more-relevant player never yanks control away
  // (this is what stops A↔B oscillation once the cooldown expires, §8.2 / D22)
  const currentScore = current && !current.sentOff ? relevanceScore(state, current, cfg) + cfg.switching.H_STICKINESS : -Infinity;
  if (bestScore - currentScore <= cfg.switching.H_MARGIN) return null; // hysteresis margin
  return bestId;
}
